import {
    Accordion,
    AccordionBody,
    AccordionHeader,
    AccordionItem,
    Form,
    Modal, Offcanvas
} from "react-bootstrap";
import {useEffect, useRef, useState} from "react";
import {Link, useNavigate} from "react-router-dom";
import {Helmet} from "react-helmet";
import {HexColorPicker} from "react-colorful";
import Loader from "./Loader";
import ScreenshotImage from "./screenshot.png";


// Default per-mortgage colours (CVD-validated, blue-led) — matches the dashboard palette.
// A new mix is seeded with a distinct colour by its index; the user can change it.
const DEFAULT_PALETTE = ["#2a78d6", "#1baf7a", "#eda100", "#008300", "#4a3aa7"];




const PERIOD_LABELS = ["תקופה וריבית ראשונה", "תקופה וריבית שנייה", "תקופה וריבית שלישית"];

const HELP_STEPS = [
    <>לוחצים על <b>"הוסף תמהיל חדש"</b></>,
    <>מזינים שם לתמהיל החדש ולוחצים על <b>"צור תמהיל"</b> (בחלון שיפתח)</>,
    <>בתמהיל החדש לוחצים על <b>"הוסף הלוואה"</b></>,
    <>בטופס שיפתח מוסיפים את פרטי ההלוואה החדשה (חשוב לתת לה שם שתזהו אותה)</>,
    <>לוחצים על <b>"הוסף הלוואה"</b> וההלוואה מתווספת לתמהיל</>,
    <>ממשיכים להוסיף הלוואות ותמהילים לפי הצורך, לבסוף לוחצים על <b>"שמור וחשב תרחישים"</b>, זה יעביר אתכם לדשבורד</>,
    <>ההשוואה נעשית בין תמהילים, כך שאם רוצים להשוות בין הלוואות (במקום משכנתאות) יש לוודא שבכל תמהיל יש רק הלוואה אחת</>,
];

function MortgageColorSwatch({color, onChange}){
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const current = color || DEFAULT_PALETTE[0];

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="mortgage-color" ref={ref}>
      <button type="button" className="mortgage-color-swatch" style={{background: current}}
              onClick={() => setOpen((o) => !o)} aria-label="בחירת צבע לתמהיל" title="בחירת צבע לתמהיל"/>
      {open && (
        <div className="mortgage-color-popover">
          <HexColorPicker color={current} onChange={onChange}/>
          <div className="mortgage-color-hex">{current}</div>
        </div>
      )}
    </div>
  );
}

function Mortgage({singleMortData, onDeleteLoan, onNewLoanClicked, removeMortgage, onEditLoan, onColorChange}){
  const mname = singleMortData['name'];
  const loans = singleMortData['loans'];
    return <div className="mortgage-card fade-in">

        <div className="mortgage-card-header">
            <div className="mortgage-title">
                <MortgageColorSwatch color={singleMortData.color} onChange={(c) => onColorChange(mname, c)}/>
                <h4 className="mortgage-name">{mname}</h4>
                <span className="loan-count-badge">{loans.length} הלוואות</span>
            </div>
            <div className="mortgage-actions">
                <button className="icon-btn icon-btn-add" onClick={() => onNewLoanClicked(mname)}>
                    <i className="bi bi-plus-lg"></i> הוסף הלוואה
                </button>
                <button className="icon-btn icon-btn-danger" onClick={() => removeMortgage(mname)}>
                    <i className="bi bi-trash"></i> מחק תמהיל
                </button>
            </div>
        </div>

        {loans.length === 0 ? (
            <p className="mortgage-empty-hint">אין עדיין הלוואות בתמהיל זה, לחצו על "הוסף הלוואה" כדי להתחיל</p>
        ) : (
        <Accordion className="loan-accordion">
            {
                loans.map((loan, index) => (
                    <AccordionItem eventKey={index} key={loan.name}>
                        <AccordionHeader>
                            <span className="loan-header-name">{loan.name}</span>
                            <span className="loan-chip">{Number(loan.principal).toLocaleString()} ₪</span>
                        </AccordionHeader>
                        <AccordionBody>
                            <div className="loan-stats-grid">
                                <div className="loan-stat">
                                    <span className="loan-stat-label">קרן</span>
                                    <span className="loan-stat-value">{Number(loan.principal).toLocaleString()}</span>
                                </div>
                                <div className="loan-stat">
                                    <span className="loan-stat-label">לוח סילוקין</span>
                                    <span className="loan-stat-value">{loan.type === "normal" ? "שפיצר" : "-"}</span>
                                </div>
                                <div className="loan-stat">
                                    <span className="loan-stat-label">הצמדה</span>
                                    <span className="loan-stat-value">{loan.index === null ? "ללא" : loan.index === "cpi" ? "מדד המחירים לצרכן" : "מדד תשומות הבניה"}</span>
                                </div>
                                <div className="loan-stat">
                                    <span className="loan-stat-label">נלקחת לסה"כ</span>
                                    <span className="loan-stat-value">{loan.periods} חודשים</span>
                                </div>
                            </div>
                            <div className="loan-periods-grid">
                                {PERIOD_LABELS.map((label, i) => {
                                    const period = loan.annual_interest[i];
                                    const hasPeriod = period && period.period !== null;
                                    return (
                                        <div className="loan-stat" key={i}>
                                            <span className="loan-stat-label">{label}</span>
                                            <span className="loan-stat-value">{hasPeriod ? `${period.period} חודשים, ${period.interest_annual}%` : "-"}</span>
                                        </div>
                                    );
                                })}
                            </div>
                            <div className="loan-detail-actions">
                                <button className="icon-btn icon-btn-edit" onClick={() => {onEditLoan(mname, loan)}}>
                                    <i className="bi bi-pencil"></i> ערוך
                                </button>
                                <button className="icon-btn icon-btn-danger" onClick={() => {onDeleteLoan(mname, loan.name)}}>
                                    <i className="bi bi-trash"></i> הסר הלוואה
                                </button>
                            </div>
                        </AccordionBody>
                    </AccordionItem>
                ))
            }
        </Accordion>
        )}
    </div>;
}



function Mortgages({mortdata, onDeleteLoanFunction, onNewLoanClicked, removeMortgage, onEditLoan, onColorChange}){
  const mortgages = mortdata.mortgages;
  return  mortgages.map((mortgage) => (<Mortgage key={mortgage.name} singleMortData={mortgage} onDeleteLoan={onDeleteLoanFunction} onNewLoanClicked={onNewLoanClicked} removeMortgage={removeMortgage} onEditLoan={onEditLoan} onColorChange={onColorChange}></Mortgage>))
}




function SimulatorPage({user,albert}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [, setError] = useState(null);
  const [saving, setSaving] = useState(false); // guards against double-submitting a save
  const [simStatus, setSimStatus] = useState(null); // "results" | "pending" | "none"
  const [showSaveConfirm, setShowSaveConfirm] = useState(false);

  const [showmodal, setShowModal] = useState(false);
  const openModal = () => setShowModal(true);
  const closeModal = () => setShowModal(false);

  const [showHelpModal, setShowHelpModal] = useState(false);

  const navigate = useNavigate();
  const [token, setToken] = useState(localStorage.getItem("token"));



  useEffect(() => {
    const fetchData = async () =>{

      const storedToken = localStorage.getItem("token");
      const startedAt = Date.now();

      console.log("Loaded token:", storedToken);
      if (storedToken) {
        setToken(storedToken);
      }

      try {
        const response = await fetch("/api/getuserjson",  {
          method: "POST",
          headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`
          }});
        if (!response.ok) {
          throw new Error("bad request");
        }
        const results = await response.json();
        setData(results);
      } catch (error) {

        setData({mortgages:[]});
        setError(error.message);
      } finally {
        // keep the spinner on screen for at least ~600ms so a fast load doesn't just flash
        const elapsed = Date.now() - startedAt;
        setTimeout(() => setLoading(false), Math.max(0, 600 - elapsed));
      }
    };
    fetchData();
    refreshSimStatus();
    // token is read once on mount; refetching on token change is intentional-only here
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Lightweight results state for the toolbar banner: results | pending | none.
  const refreshSimStatus = async () => {
    try {
      const r = await fetch("/api/simstatus", {
        method: "GET",
        headers: {Authorization: `Bearer ${localStorage.getItem("token")}`},
      });
      if (!r.ok) return;
      const j = await r.json();
      setSimStatus(j.status);
    } catch (e) { /* best-effort banner */ }
  };

  // delete loans
  const handleDelete = (mortgageName, loanName) => {
    const updatedMortgages = data.mortgages.map(mortgage => {
      if (mortgage.name ===  mortgageName) {
        mortgage.loans = mortgage.loans.filter(loan => loan.name !== loanName);
      }
      return mortgage;
    });
    // spread ...data so sibling sections (e.g. assets) aren't dropped from the saved JSON
    setData(data => ({...data, mortgages: updatedMortgages}));
  };


  // create new mortgage
  const [newmortgagename, setValueNewMortgageName] = useState("");
  const handleNewMortgage = () => {
    if (newmortgagename.length < 2) {
      albert("חובה להקליד שם למשכנתא");
      return; // keep the modal open so the user can fix the name
    }
    if (data.mortgages.map(m => m.name).includes(newmortgagename)) {
      albert("שם התמהיל כבר קיים, יש לבחור שם אחר");
      return;
    }
    setData(data => ({...data, mortgages:[...data.mortgages, {
      name: newmortgagename,
      loans: [],
      color: DEFAULT_PALETTE[data.mortgages.length % DEFAULT_PALETTE.length],
    }]}));
    setValueNewMortgageName("");
    closeModal();
  };

  // update a mortgage's chosen colour (persisted with the mix JSON on save)
  const handleColorChange = (mortgageName, color) => {
    setData(data => ({...data, mortgages: data.mortgages.map(m =>
      m.name === mortgageName ? {...m, color} : m
    )}));
  };

  const [showoffcanvas, setShowOffCanvas] = useState(false);
  const [handlemortgage, setHandleMortgage] = useState(null);
  const [editingLoanName, setEditingLoanName] = useState(null);
  const [newlaonName, setNewLoanName] = useState(null);
  const [newloanPrincipal, setNewLoanPrincipal] = useState(null);

  const [shanim1, setShanim1] = useState(null);
  const [interest1, setInterest1] = useState(null);

  const [shanim2, setShanim2] = useState(null);
  const [interest2, setInterest2] = useState(null);

  const [shanim3, setShanim3] = useState(null);
  const [interest3, setInterest3] = useState(null);

  const [selectedIndex, setSelectedIndex] = useState(null);

  const [selectedAnchor, setSelectedAnchor] = useState(null);
  const anchore_freq_parser = {"prime": 1, "bond-5": 12*5, "bond-3": 12*3}; // in months

  //opens the off canvas
  const setNewLoanForMortgage = (mortgagename) => {
    setHandleMortgage(mortgagename); // controls which mortgage will be added a new loan
    setEditingLoanName(null);
    setNewLoanName(null);
    setNewLoanPrincipal(null);
    setShanim1(null);
    setInterest1(null);
    setShanim2(null);
    setInterest2(null);
    setShanim3(null);
    setInterest3(null);
    setSelectedIndex(null);
    setSelectedAnchor(null);
    setShowOffCanvas(true);
  };

  // opens the off canvas prefilled with an existing loan's data for editing
  const openEditLoan = (mortgagename, loan) => {
    setHandleMortgage(mortgagename);
    setEditingLoanName(loan.name);
    setNewLoanName(loan.name);
    setNewLoanPrincipal(loan.principal);
    setShanim1(loan.annual_interest[0]?.period ?? null);
    setInterest1(loan.annual_interest[0]?.interest_annual ?? null);
    setShanim2(loan.annual_interest[1]?.period ?? null);
    setInterest2(loan.annual_interest[1]?.interest_annual ?? null);
    setShanim3(loan.annual_interest[2]?.period ?? null);
    setInterest3(loan.annual_interest[2]?.interest_annual ?? null);
    setSelectedIndex(loan.index ?? null);
    setSelectedAnchor(loan.anchor && loan.anchor !== 'None' ? loan.anchor.type : null);
    setShowOffCanvas(true);
  };

  // user clicked on save loan
  const handleSaveLoan = () => {
    const periods = [shanim1, shanim2, shanim3];
    const selected_mortgage = data.mortgages.find(item => item.name === handlemortgage);
    // a loan name collides only with a *different* existing loan (not the one being edited)
    const exist = selected_mortgage.loans.find(loan => loan.name === newlaonName && loan.name !== editingLoanName);

    // Collect the validation errors in priority order and show only the FIRST one, as a single
    // popup. Previously each failed check fired its own albert(), so several alerts stacked up
    // (and the loan could even be saved anyway) — one clear message is enough.
    const errors = [];
    if (newlaonName === null) errors.push("שם ההלוואה חסר");
    if (newloanPrincipal === null) errors.push("קרן ההלוואה חסרה");
    else if (isNaN(Number(newloanPrincipal))) errors.push("קרן ההלוואה חייבת להיות מספרית");
    if (shanim1 === null) errors.push("לכמה שנים תיקח את ההלוואה?");
    else if (isNaN(Number(shanim1))) errors.push("מספר התקופות חייב להיות מספר שלם");
    if (interest1 === null) errors.push("באיזו ריבית תיקח את ההלוואה?");
    else if (isNaN(Number(interest1))) errors.push("הריבית חייבת להיות מספרית");
    if ((interest2 === null) !== (shanim2 === null)) errors.push("נתונים לא תקינים בתקופה 2");
    if ((interest3 === null) !== (shanim3 === null)) errors.push("נתונים לא תקינים בתקופה 3");
    if (exist !== undefined) errors.push("שם ההלוואה כבר קיים, יש להשתמש בשם אחר");

    if (errors.length > 0) {
      albert(errors[0]);
      return; // keep the form open so the user can fix the issue
    }

    const newloan = {
        name: newlaonName,
        type: "normal",
        principal: Number(newloanPrincipal),
        annual_interest:
          [
              {period: Number(shanim1), interest_annual: Number(interest1)},
              {period: Number(shanim2), interest_annual: Number(interest2)},
              {period: Number(shanim3), interest_annual: Number(interest3)}
          ],
        periods: periods.reduce((acc, num) => acc + (Number(num) ||  0), 0),
        index: selectedIndex,
        anchor: selectedAnchor !== null ? {type: selectedAnchor, freq: anchore_freq_parser[selectedAnchor], mean: 0, var: 0.05} : 'None',
    };

    if (editingLoanName !== null) {
        selected_mortgage.loans = selected_mortgage.loans.map(loan => loan.name === editingLoanName ? newloan : loan);
    } else {
        selected_mortgage.loans = [...selected_mortgage.loans, newloan];
    }
    setData({...data});
    setShowOffCanvas(false);
  };

  if (user.id === null) return (
        <>
            <Helmet>
                <title>מחשבון משכנתא | דוח תרחישים | דשבורד </title>
                <meta name="description" content="דוח תרחישים וניתוח תוצאות של המחשבון" />
            </Helmet>
            <center>
                <br/>
                <h4>דשבורד לדוגמא</h4>
                <p>להצגת התוצאות יש ליצור חשבון ולהתחבר.</p>
                <img src={ScreenshotImage} className="img-fluid mb-3 dashboardimage " alt="מחשבון" />
                <br/>
                <Link to="/login" className="align-items-center homepagebutton">כניסה</Link>
                <Link to="/register" className="align-items-center homepagebutton">הרשמה</Link>
            </center>
        </>
    );

  if (loading) return <Loader title="טוען את התמהילים שלך" text="רק רגע, מכינים את סביבת העריכה"/>;
  // if (error) {return <div><Mortgages mortdata={data} onDeleteLoanFunction={handleDelete}></Mortgages><Button>הוסף משכנתא</Button></div>};

  const handleSendData = async ({just_save}) => {
    if (saving) return; // ignore repeat clicks while a save is already in flight
    if (data.mortgages.length === 0) {
      albert("יש להזין הלוואות ותמהילים להשוואה");
      return;
    }
    setSaving(true);
    try {
      // "שמור" (just_save) clears stale results without recomputing; "שמור וחשב" recomputes.
      const flags = just_save ? {run_simulation: false, clear_results: true} : {run_simulation: true};
      const response = await fetch('/api/putuserjson', {
        method: "POST", // אפשר לשנות ל-"PUT" לפי הצורך
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}` ,
        },
        body: JSON.stringify({userid: localStorage.getItem("userid"), userjson: JSON.stringify(data), ...flags}),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const result = await response.json(); // אם השרת מחזיר JSON
      setData(result);
      albert(just_save
        ? "התמהילים נשמרו. תוצאות הסימולציה הקודמות נמחקו — הריצו סימולציה כדי לראות תוצאות מעודכנות."
        : "הנתונים נשמרו");
      refreshSimStatus();
      if (!just_save){
        setTimeout(() => navigate("/dashboard"), 1500);
      }

    } catch (error) {
      albert("רק משתמש רשום ומחובר יכול לשלוח נתונים לעיבוד");
    } finally {
      setSaving(false);
    }
  };

  const removeMortgage = (mname) => {
      setData(data => ({...data, mortgages:[...data.mortgages.filter(x => x.name !== mname)]}));
  };

    return <div style={{padding: "15px"}}>
        <Helmet>
            <title>מחשבון משכנתא | עריכת התמהילים </title>
            <meta name="description" content="דך עריכת התמהילים"/>
        </Helmet>

        {saving && (
            <div className="app-loading-overlay">
                <div className="loadeing" role="status" aria-label="שומר"></div>
                <h5 className="app-loader-title">שומר את הנתונים...</h5>
                <p className="app-loader-text">עוד רגע ומסיימים</p>
            </div>
        )}
        <Modal show={showmodal} onHide={closeModal} className="mortgage-modal" centered >
            <Modal.Header closeButton>
                <Modal.Title>
                    <i className="bi bi-folder-plus"></i>
                    הוספת תמהיל חדש
                </Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <Form onSubmit={(e) => {e.preventDefault(); handleNewMortgage();}}>
                    <div className="loan-form-section">
                        <Form.Label htmlFor="inputNewMortgageName"><i className="bi bi-tag"></i> שם לתמהיל</Form.Label>
                        <Form.Control
                            type="input"
                            id="inputNewMortgageName"
                            aria-describedby="newMortgageNameHint"
                            onChange={(e) => setValueNewMortgageName(e.target.value)}
                            value={newmortgagename}
                            autoFocus
                            required
                        />
                        <div className="loan-form-hint" id="newMortgageNameHint">
                            אין להשתמש בשמות שכבר קיימים. מומלץ לתת שמות לפי נותני המשכנתאות, לדוגמה "בנק הפועלים 1"
                        </div>
                    </div>
                </Form>
            </Modal.Body>

            <Modal.Footer>
                <button className="abort" onClick={closeModal}>בטל</button>
                <button className="addnew" onClick={handleNewMortgage}><i className="bi bi-plus-lg"></i> צור תמהיל</button>
            </Modal.Footer>
        </Modal>
        {/* OffCanvas come here */}
        <Offcanvas show={showoffcanvas} placement="end" onHide={() => setShowOffCanvas(false)} className="loan-offcanvas" backdropClassName="loan-offcanvas-backdrop">
            <Offcanvas.Header closeButton>
                <Offcanvas.Title>
                    <i className={`bi ${editingLoanName !== null ? "bi-pencil-square" : "bi-plus-circle"}`}></i>
                    {editingLoanName !== null ? `עריכת הלוואה: ${editingLoanName}` : `הוספת הלוואה לתמהיל: ${handlemortgage}`}
                </Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body className="loan-form-body">
                <Form>
                    <div className="loan-form-section">
                        <Form.Label htmlFor="newloanName"><i className="bi bi-tag"></i> שם ההלוואה</Form.Label>
                        <Form.Control
                            id="newloanName"
                            type="input"
                            value={newlaonName}
                            onChange={(e) => {
                                setNewLoanName(e.target.value)
                            }}/>
                        <div className="loan-form-hint">שם ייחודי בתוך התמהיל, לדוגמה "משכנתא שפיצר 20 שנה"</div>
                    </div>

                    <div className="loan-form-section">
                        <Form.Label htmlFor="newloanPrincipal"><i className="bi bi-cash-stack"></i> קרן ההלוואה</Form.Label>
                        <Form.Control
                            id="newloanPrincipal"
                            type="number"
                            value={newloanPrincipal}
                            onChange={(e) => {
                                setNewLoanPrincipal(e.target.value)
                            }}/>
                    </div>

                    <div className="loan-form-section">
                        <Form.Label><i className="bi bi-calendar3"></i> תקופות וריבית</Form.Label>
                        <div className="loan-form-hint">בריבית קבועה יש להזין רק תקופה 1. לדירוג ריביות לפי תקופות ניתן להוסיף גם תקופה 2 ו-3</div>

                        <div className="period-row">
                            <span className="numbered-badge">1</span>
                            <div className="period-fields">
                                <div className="period-field">
                                    <label>חודשים</label>
                                    <Form.Control type="number" step="1" aria-label="years1"
                                                  value={shanim1} onChange={(e) => setShanim1(e.target.value)}/>
                                </div>
                                <div className="period-field">
                                    <label>ריבית שנתית %</label>
                                    <Form.Control type="number" step="0" aria-label="interest1"
                                                  value={interest1} onChange={(e) => setInterest1(e.target.value)}/>
                                </div>
                            </div>
                        </div>
                        <div className="period-row">
                            <span className="numbered-badge">2</span>
                            <div className="period-fields">
                                <div className="period-field">
                                    <label>חודשים</label>
                                    <Form.Control type="number" step="1" aria-label="years2"
                                                  value={shanim2} onChange={(e) => setShanim2(e.target.value)}/>
                                </div>
                                <div className="period-field">
                                    <label>ריבית שנתית %</label>
                                    <Form.Control type="number" step="0" aria-label="interest2"
                                                  value={interest2} onChange={(e) => setInterest2(e.target.value)}/>
                                </div>
                            </div>
                        </div>
                        <div className="period-row">
                            <span className="numbered-badge">3</span>
                            <div className="period-fields">
                                <div className="period-field">
                                    <label>חודשים</label>
                                    <Form.Control type="number" step="1" aria-label="years3"
                                                  value={shanim3} onChange={(e) => setShanim3(e.target.value)}/>
                                </div>
                                <div className="period-field">
                                    <label>ריבית שנתית %</label>
                                    <Form.Control type="number" step="0" aria-label="interest3"
                                                  value={interest3} onChange={(e) => setInterest3(e.target.value)}/>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="loan-form-section">
                        <Form.Label htmlFor="anchor"><i className="bi bi-graph-up-arrow"></i> האם הריבית מעוגנת?</Form.Label>
                        <Form.Select id="anchor" aria-label="Select Anchor" value={selectedAnchor ?? "none"} onChange={(e) => setSelectedAnchor(e.target.value === "none" ? null : e.target.value)}>
                            <option value="none">ללא עוגן</option>
                            <option value="prime">פריים</option>
                            <option value="bond-3">אגח כל שלוש שנים</option>
                            <option value="bond-5">אגח כל חמש שנים</option>
                        </Form.Select>
                    </div>

                    <div className="loan-form-section">
                        <Form.Label htmlFor="madad1"><i className="bi bi-percent"></i> האם ההלוואה צמודת מדד?</Form.Label>
                        <Form.Select id="madad1" aria-label="Select Index" value={selectedIndex ?? "none"} onChange={(e) => setSelectedIndex(e.target.value === "none" ? null : e.target.value)}>
                            <option value="none">ללא הצמדה</option>
                            <option value="cpi-housing">מדד תשומות הבניה</option>
                            <option value="cpi">מדד המחירים לצרכן</option>
                        </Form.Select>
                    </div>
                </Form>

                <div className="loan-form-actions">
                    <button className="addnew" onClick={() => handleSaveLoan()}>
                        <i className={`bi ${editingLoanName !== null ? "bi-check-lg" : "bi-plus-lg"}`}></i>
                        {editingLoanName !== null ? "שמור שינויים" : "הוסף הלוואה"}
                    </button>
                </div>
            </Offcanvas.Body>
        </Offcanvas>

        {/*end of offcanvas*/}

        <Modal show={showHelpModal} onHide={() => setShowHelpModal(false)} className="help-modal" centered>
            <Modal.Header closeButton>
                <Modal.Title>
                    <i className="bi bi-question-circle"></i>
                    איך משתמשים
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="help-modal-body">
                <ol className="help-steps">
                    {HELP_STEPS.map((step, i) => (
                        <li className="help-step" key={i}>
                            <span className="numbered-badge">{i + 1}</span>
                            <span className="help-step-text">{step}</span>
                        </li>
                    ))}
                </ol>
                <p className="help-footnote">יש לכם רעיון לתכונה נוספת? אתם מוזמנים ליצור קשר, אנחנו מאוד זריזים :)</p>
            </Modal.Body>
            <Modal.Footer>
                <button className="abort" onClick={() => setShowHelpModal(false)}>סגור</button>
                <Link to="/contact" className="addnew" onClick={() => setShowHelpModal(false)}>צור קשר</Link>
            </Modal.Footer>
        </Modal>

        <Modal show={showSaveConfirm} onHide={() => setShowSaveConfirm(false)} className="mortgage-modal" centered>
            <Modal.Header closeButton>
                <Modal.Title>
                    <i className="bi bi-exclamation-triangle"></i>
                    מחיקת תוצאות קודמות
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p>שמירה ללא חישוב תמחק את תוצאות הסימולציה האחרונות, שכבר אינן תואמות לתמהילים המעודכנים.</p>
                <p className="loan-form-hint">כדי לראות תוצאות מעודכנות, לחצו במקום זאת על "שמור וחשב תרחישים".</p>
            </Modal.Body>
            <Modal.Footer>
                <button className="abort" onClick={() => setShowSaveConfirm(false)}>ביטול</button>
                <button className="savejson" onClick={() => { setShowSaveConfirm(false); handleSendData({just_save: true}); }}>
                    <i className="bi bi-save"></i> שמור ומחק תוצאות
                </button>
            </Modal.Footer>
        </Modal>

        <div id="margener">
            <div id="editor">
                <div className="editor-toolbar">
                    <div className="editor-toolbar-title">
                        <h2 className="my-text-gradient"> התמהילים שלי </h2>
                        <button className="help-btn" onClick={() => setShowHelpModal(true)} aria-label="איך משתמשים" title="איך משתמשים">
                            <i className="bi bi-question-circle"></i>
                        </button>
                    </div>
                    <div className="editor-toolbar-actions">
                        <button onClick={openModal} className="addnew"><i className="bi bi-plus-lg"></i> הוסף תמהיל חדש</button>
                        <button className="savejson" disabled={saving} onClick={() => {
                            // saving without recomputing clears existing results -> confirm first
                            if (simStatus === "results" || simStatus === "pending") setShowSaveConfirm(true);
                            else handleSendData({just_save: true});
                        }}><i className="bi bi-save"></i> {saving ? "שומר..." : "שמור"}</button>
                        <button className="calculate" disabled={saving} onClick={() => {handleSendData({just_save: false})}}><i className="bi bi-bar-chart-line"></i> {saving ? "שומר..." : "שמור וחשב תרחישים"}</button>
                    </div>
                </div>

                {simStatus && (
                    <div className={`sim-status-banner sim-status-banner--${simStatus}`}>
                        {simStatus === "none" && (<><i className="bi bi-exclamation-triangle"></i>
                            <span>אין תוצאות סימולציה. לחצו "שמור וחשב תרחישים" כדי להריץ.</span></>)}
                        {simStatus === "pending" && (<><i className="bi bi-hourglass-split"></i>
                            <span>הסימולציה מתבצעת כעת...</span> <Link to="/dashboard">מעבר לדשבורד</Link></>)}
                        {simStatus === "results" && (<><i className="bi bi-check-circle"></i>
                            <span>התוצאות מוכנות.</span> <Link to="/dashboard">צפייה בדשבורד</Link></>)}
                    </div>
                )}

                {data.mortgages.length === 0 ? (
                    <div className="empty-state">
                        <i className="bi bi-houses"></i>
                        <p>עדיין לא הוספתם תמהיל משכנתא. לחצו על "הוסף תמהיל חדש" כדי להתחיל.</p>
                    </div>
                ) : (
                    <div className="mortgages-grid">
                        <Mortgages mortdata={data} onDeleteLoanFunction={handleDelete}
                                   onNewLoanClicked={setNewLoanForMortgage} removeMortgage={removeMortgage}
                                   onEditLoan={openEditLoan} onColorChange={handleColorChange}></Mortgages>
                    </div>
                )}
            </div>
        </div>
    </div>;
}

export default SimulatorPage;